import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, throwError, firstValueFrom } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AlertDialogService } from '@shared/components/alert-dialog/alert-dialog-service/alert-dialog-service.service';
import { error } from 'console';

@Injectable()
export class CrudService {
  private apiUrl = `${environment.API_SERVER}/`;
  private apiNotification = `${environment.API_NOTIFICATION}/`;
  private apiTileUrl = `${environment.API_TILE}`;
  private apiTrayectoriaUrl = `${environment.API_TRAYECTORIA}`;
  private apiStatictis = `${environment.API_STATISTIC}`;
  private apiTeltonika = `${environment.API_TELTONIKA}`;
  private apiIvTramas = `${environment.IVTRAMAS}`;
  private apiTramas = `${environment.API_TRAMAS}`;
  private apiGeo = `${environment.API_GEO}`;
  private apiLogin = `${environment.API_URL}api-login`;
  private apiPacket = `${environment.API_PACKET}`;
  private apiMantenimiento = `${environment.API_MANTENIMIENTO}`;
  private apiGeofencing = `${environment.API_GEOFENCING}`;

  public token: string | null = null;
  public authenticationHeaders!: HttpHeaders;
  public authenticationHeadersStatistics!: HttpHeaders;

  constructor(private http: HttpClient, private router: Router, private _alertDialogService: AlertDialogService) {
    if (this.router.url !== '/') {
      this.initHeaders();
    }
  }

  initHeaders(): void {
    const token = this.getToken();
    this.authenticationHeaders = new HttpHeaders()
      .set('x-access-token', token || '')
      .set('Content-Type', 'application/json')
      .set('Accept', 'application/json');
    this.authenticationHeadersStatistics = new HttpHeaders().set(
      'Authorization',
      token || ''
    );
  }

  getToken(): string | null {
    const token = localStorage.getItem('id_token');
    this.token = token !== 'undefined' ? token : null;
    return this.token;
  }

  public getUrlATSSERVER(): string {
    return this.apiUrl;
  }

  public get<T>(entityName: string, params?: any): Promise<any> {
    return firstValueFrom(
      this.http
        .get<any>(this.apiUrl + entityName, {
          headers: this.authenticationHeaders,
          params,
        })
        .pipe(catchError((e) => this.processError(e)))
    );
  }

  public getNotification<T>(entityName: string, params?: any): Promise<any> {
    return firstValueFrom(
      this.http
        .get<any>(this.apiNotification + entityName, {
          headers: this.authenticationHeaders,
          params,
        })
        .pipe(catchError((e) => this.processError(e)))
    );
  }

  public get2<T>(entityName: string, param: string): Promise<any> {
    return firstValueFrom(
      this.http
        .get<any>(environment.API_URL + entityName + '/' + param)
        .pipe(catchError((e) => this.processError(e)))
    );
  }

  public getQueryParams<T>(): Promise<any> {
    return firstValueFrom(
      this.http
        .get<any>(this.apiUrl, { withCredentials: true })
        .pipe(catchError((e) => this.processError(e)))
    );
  }

  public getEntities<T>(entityName: string, params?: any): Promise<any> {
    return firstValueFrom(
      this.http
        .get<any>(this.apiUrl + entityName + '/getAll', {
          headers: this.authenticationHeaders,
          params,
        })
        .pipe(catchError((e) => this.processError(e)))
    );
  }

  public getEntity<T>(entityName: string, id: any): Observable<any> {
    return this.http
      .get<any>(this.apiUrl + entityName + '/get/' + id, {
        headers: this.authenticationHeaders,
      })
      .pipe(catchError((e) => this.processError(e)));
  }

  public getLogin<T>(id: any): Observable<any> {
    return this.http
      .get<any>(this.apiLogin + '/get-user/' + id, {
        headers: this.authenticationHeaders,
      })
      .pipe(catchError((e) => this.processError(e)));
  }

  public getLoginUser<T>(): Observable<any> {
    return this.http
      .get<any>(this.apiLogin + '/get-All', {
        headers: this.authenticationHeaders,
      })
      .pipe(catchError((e) => this.processError(e)));
  }

  public getManyEntity<T>(
    entityName: string,
    field: string,
    value: string
  ): Observable<T[]> {
    return this.http
      .get<T[]>(this.apiUrl + entityName + '/getMany/' + field + '/' + value, {
        headers: this.authenticationHeaders,
      })
      .pipe(catchError((e) => this.processError(e)));
  }

  public getStatistic<T>(url: string, entity: T): Observable<any> {
    return this.http
      .post<any>(this.apiStatictis + url, entity, {
        headers: this.authenticationHeadersStatistics,
      })
      .pipe(catchError((e) => this.processError(e)));
  }

  public getActuatorSensorValues(mac: string): Observable<any> {
    return this.http
      .get<any>(`${this.apiIvTramas}sensorValues/getLast/mac/${mac}`, {
        headers: this.authenticationHeaders,
      })
      .pipe(catchError((e) => this.processError(e)));
  }

  public countEntities<T>(entityName: string): Observable<T[]> {
    return this.http
      .get<T[]>(this.apiUrl + entityName + '/count/', {
        headers: this.authenticationHeaders,
      })
      .pipe(catchError((e) => this.processError(e)));
  }

  public post<T>(entityName: string, body: any, params?: any): Promise<any> {
    return firstValueFrom(
      this.http
        .post<any>(this.apiUrl + entityName, body, {
          headers: this.authenticationHeaders,
          params,
        })
        .pipe(catchError((e) => this.processError(e)))
    );
  }

  public existsEntity<T>(entityName: string, id: string): Observable<T[]> {
    return this.http
      .post<T[]>(this.apiUrl + entityName + '/exists/', id, {
        headers: this.authenticationHeaders,
      })
      .pipe(catchError((e) => this.processError(e)));
  }

  public createEntity<T>(entityName: string, entity: T): Observable<any> {
    return this.http
      .post<any>(this.apiUrl + entityName + '/create', entity, {
        headers: this.authenticationHeaders,
      })
      .pipe(catchError((e) => this.processError(e)));
  }

  public createNotification<T>(entityName: string, entity: T): Observable<any> {
    return this.http
      .post<any>(this.apiNotification + entityName + '/create', entity, {
        headers: this.authenticationHeaders,
      })
      .pipe(catchError((e) => this.processError(e)));
  }

  public getEntitiesNotification<T>(
    entityName: string,
    params: any
  ): Promise<any> {
    return firstValueFrom(
      this.http
        .get<any>(this.apiNotification + entityName + '/getAll', {
          headers: this.authenticationHeaders,
          params,
        })
        .pipe(catchError((e) => this.processError(e)))
    );
  }

  public getEntitiesNotificationAll<T>(
    entityName: string,
    params: any
  ): Promise<any> {
    return firstValueFrom(
      this.http
        .get<any>(this.apiNotification + entityName + '/getByAlertLevel/3', {
          headers: this.authenticationHeaders,
          params,
        })
        .pipe(catchError((e) => this.processError(e)))
    );
  }

  public createLogin<T>(data: any): Observable<any> {
    return this.http
      .post<any>(this.apiLogin + '/create', data, {
        headers: this.authenticationHeaders,
      })
      .pipe(catchError((e) => this.processError(e)));
  }

  public setDoutSimple<T>(entity: T): Observable<any> {
    return this.http
      .post<any>(
        this.apiTeltonika + 'commandController/setDoutSimple',
        entity,
        { headers: this.authenticationHeaders }
      )
      .pipe(catchError((e) => this.processError(e)));
  }

  public createMany<T>(entityName: string, entity: T): Observable<any> {
    return this.http
      .post<any>(this.apiUrl + entityName + '/createMany', entity, {
        headers: this.authenticationHeaders,
      })
      .pipe(catchError((e) => this.processError(e)));
  }

  public put<T>(entityName: string, entity: T): Observable<any> {
    return this.http
      .put<any>(this.apiUrl + entityName, entity, {
        headers: this.authenticationHeaders,
      })
      .pipe(catchError((e) => this.processError(e)));
  }

  public updateEntity<T>(entityName: string, entity: T): Observable<any> {
    return this.http
      .put<any>(this.apiUrl + entityName + '/update/', entity, {
        headers: this.authenticationHeaders,
      })
      .pipe(catchError((e) => this.processError(e)));
  }

  public updateEntityMasive<T>(entityName: string, entity: T): Observable<any> {
    return this.http
      .post<any>(
        this.apiNotification + entityName + '/multipleEvents/',
        entity,
        { headers: this.authenticationHeaders }
      )
      .pipe(catchError((e) => this.processError(e)));
  }

  public updateLogin<T>(entity: T): Observable<any> {
    return this.http
      .put<any>(this.apiLogin + '/update/', entity, {
        headers: this.authenticationHeaders,
      })
      .pipe(catchError((e) => this.processError(e)));
  }

  public updateMany<T>(entityName: string, entity: T): Observable<any> {
    return this.http
      .put<any>(this.apiUrl + entityName + '/updateMany', entity, {
        headers: this.authenticationHeaders,
      })
      .pipe(catchError((e) => this.processError(e)));
  }

  /**
   * @param idItem Array de IDs de items a actualizar
   * @param updateData Objeto con los campos a actualizar (idEstadoItem, indCondicion, fechaEstado, etc.)
   */
  public updateManyItems<T>(
    idItem: number[],
    updateData: any
  ): Observable<any> {
    const payload = {
      idItem,
      ...updateData
    };
    return this.http
      .put<any>(this.apiUrl + 'item/updateMany', payload, {
        headers: this.authenticationHeaders,
      })
      .pipe(catchError((e) => this.processError(e)));
  }

  /**
   * @param idPersona Array de IDs de personas a actualizar
   * @param updateData Objeto con los campos a actualizar (idEstadoPersona, indCondicion, fechaEstado, etc.)
   */
  public updateManyPersonas<T>(
    idPersona: number[],
    updateData: any
  ): Observable<any> {
    const payload = {
      idPersona,
      ...updateData
    };
    return this.http
      .put<any>(this.apiUrl + 'person/updateMany', payload, {
        headers: this.authenticationHeaders,
      })
      .pipe(catchError((e) => this.processError(e)));
  }

  public updateEntityWithId<T>(
    entityName: string,
    entity: T,
    entityId: any
  ): Observable<any> {
    return this.http
      .put<any>(this.apiUrl + entityName + '/update/' + entityId, entity, {
        headers: this.authenticationHeaders,
      })
      .pipe(catchError((e) => this.processError(e)));
  }

  public updatePacket<T>(entityName: string): Observable<any> {
    return this.http
      .post<any>(
        this.apiPacket + entityName,
        {},
        { headers: this.authenticationHeaders }
      )
      .pipe(catchError((e) => this.processError(e)));
  }

  public updateEntities<T>(entityName: string, entities: T): Observable<any> {
    return this.http
      .put<any>(this.apiUrl + entityName + '/update/', entities, {
        headers: this.authenticationHeaders,
      })
      .pipe(catchError((e) => this.processError(e)));
  }

  public lastPosition<T>(
    entityName: string,
    entity: T,
    entityId: any
  ): Observable<any> {
    return this.http
      .put<any>(
        this.apiTileUrl + '/lastPosition/' + entityName + entityId,
        entity,
        { headers: this.authenticationHeaders }
      )
      .pipe(catchError((e) => this.processError(e)));
  }

  public getlastPosition<T>(): Observable<any> {
    return this.http
      .get<any>(this.apiTileUrl + '/lastPosition/multipleUsers', {
        headers: this.authenticationHeaders,
      })
      .pipe(catchError((e) => this.processError(e)));
  }

  public lastPositionMany<T>(entityName: string, entity: T): Observable<any> {
    return this.http
      .put<any>(this.apiTileUrl + '/lastPosition/' + entityName, entity, {
        headers: this.authenticationHeaders,
      })
      .pipe(catchError((e) => this.processError(e)));
  }

  public deleteEntityWithBody<T>(
    entityName: string,
    entityId: any
  ): Observable<any> {
    return this.http
      .request<any>('delete', this.apiUrl + entityName + '/delete', {
        body: entityId,
        headers: this.authenticationHeaders,
      })
      .pipe(catchError((e) => this.processError(e)));
  }

  public lastPositionItem<T>(
    entityName: string,
    entity: T,
    entityId: any
  ): Observable<any> {
    return this.http
      .put<any>(
        this.apiTileUrl + '/lastPositionItem/' + entityName + entityId,
        entity,
        { headers: this.authenticationHeaders }
      )
      .pipe(catchError((e) => this.processError(e)));
  }

  public lastPositionItemMany<T>(
    entityName: string,
    entity: T
  ): Observable<any> {
    return this.http
      .put<any>(this.apiTileUrl + '/lastPositionItem/' + entityName, entity, {
        headers: this.authenticationHeaders,
      })
      .pipe(catchError((e) => this.processError(e)));
  }

  /**
   * @param idsPersona Array de IDs de personas
   * @param updateBody Objeto con los campos a actualizar (ej: { 'properties.estado': 3 })
   * @param idEmpresaPadre ID de la empresa padre
   */
  public updateLastPositionManyPersonas<T>(
    idsPersona: number[],
    updateBody: any,
    idEmpresaPadre: number
  ): Observable<any> {
    const payload = {
      idsPersona,
      updateBody,
      idEmpresaPadre
    };
    return this.http
      .put<any>(this.apiTileUrl + '/lastPosition/updateMany', payload, {
        headers: this.authenticationHeaders,
      })
      .pipe(catchError((e) => this.processError(e)));
  }

  /**
   * @param idsItem Array de IDs de items
   * @param updateBody Objeto con los campos a actualizar (ej: { 'properties.estado': 12 })
   * @param idEmpresaPadre ID de la empresa padre
   */
  public updateLastPositionManyItems<T>(
    idsItem: number[],
    updateBody: any,
    idEmpresaPadre: number
  ): Observable<any> {
    const payload = {
      idsItem,
      updateBody,
      idEmpresaPadre
    };
    return this.http
      .put<any>(this.apiTileUrl + '/lastPositionItem/updateMany', payload, {
        headers: this.authenticationHeaders,
      })
      .pipe(catchError((e) => this.processError(e)));
  }

  public getGlobalCounts<T>(body?: any, params?: any): Promise<any> {
    return firstValueFrom(
      this.http
        .post<any>(this.apiGeo + '/count/getGlobalCounts/', body, {
          headers: this.authenticationHeaders,
          params,
        })
        .pipe(catchError((e) => this.processError(e)))
    );
  }

  public getTrajectories<T>(body?: any, params?: any): Promise<any> {
    return firstValueFrom(
      this.http
        .post<any>(
          this.apiTrayectoriaUrl + '/trajectory/getTrajectories/',
          body,
          { headers: this.authenticationHeaders, params }
        )
        .pipe(catchError((e) => throwError(() => e))) // Sin processError()

    );
  }

  public getTrajectoriesTimeSegment<T>(body?: any, params?: any): Promise<any> {
    return firstValueFrom(
      this.http
        .post<any>(
          this.apiTrayectoriaUrl + '/trajectory/getAllActiveTrajectory',
          body,
          { headers: this.authenticationHeaders, params }
        )
        .pipe(catchError((e) => throwError(() => e))) // Sin processError()

    );
  }

  public deleteEntityMongo<T>(id: string): Observable<any> {
    const options = {
      headers: this.authenticationHeaders,
      body: { idPersona: id },
    };
    return this.http
      .delete<any>(this.apiUrl + 'person/delete', options)
      .pipe(catchError((e) => this.processError(e)));
  }

  public deleteEntity<T>(entityName: string, id: string): Observable<any> {
    return this.http
      .delete<any>(this.apiUrl + entityName + '/delete/' + id, {
        headers: this.authenticationHeaders,
      })
      .pipe(catchError((e) => this.processError(e)));
  }

  public deleteLogin<T>(id: string): Observable<any> {
    return this.http
      .delete<any>(this.apiLogin + '/delete/' + id, {
        headers: this.authenticationHeaders,
      })
      .pipe(catchError((e) => this.processError(e)));
  }

  public deleteEntities<T>(
    entityName: string,
    field: string,
    value: string
  ): Observable<any> {
    return this.http
      .delete<any>(
        this.apiUrl + entityName + '/delete/' + field + '/' + value,
        { headers: this.authenticationHeaders }
      )
      .pipe(catchError((e) => this.processError(e)));
  }

  public deleteAllEntities<T>(entityName: string): Observable<any> {
    return this.http
      .delete<any>(this.apiUrl + entityName + '/deleteAll/', {
        headers: this.authenticationHeaders,
      })
      .pipe(catchError((e) => this.processError(e)));
  }

  public delete<T>(entityName: string): Observable<any> {
    return this.http
      .delete<any>(this.apiUrl + entityName + entityName, {
        headers: this.authenticationHeaders,
      })
      .pipe(catchError((e) => this.processError(e)));
  }

  public deleteWithBody<T>(entityName: string, entityId: any): Observable<any> {
    return this.http
      .request<any>('delete', this.apiUrl + entityName, {
        body: entityId,
        headers: this.authenticationHeaders,
      })
      .pipe(catchError((e) => this.processError(e)));
  }

  public getTrama<T>(id: string): Observable<any> {
    return this.http
      .get<any>(this.apiTramas + id)
      .pipe(catchError((e) => this.processError(e)));
  }

  public deleteQueryParams<T>(
    entityName: string,
    params: any
  ): Observable<any> {
    return this.http
      .delete<any>(this.apiUrl + entityName + '/delete/' + params, {
        headers: this.authenticationHeaders,
      })
      .pipe(catchError((e) => this.processError(e)));
  }

  public deleteFile<T>(entityName: string, params: any): Observable<any> {
    return this.http
      .delete<any>(this.apiUrl + entityName + params, {
        headers: this.authenticationHeaders,
      })
      .pipe(catchError((e) => this.processError(e)));
  }

  public sendEmail<T>(entityName: string, email: any): Observable<any> {
    return this.http
      .get<any>(this.apiLogin + entityName + '/' + email)
      .pipe(catchError((e) => this.processError(e)));
  }

  public downloadFile<T>(
    name: string,
    url: string,
    type: string
  ): Observable<any> {
    const headers = new HttpHeaders().set(
      'x-access-token',
      this.getToken() || ''
    );
    return this.http
      .get(url, { responseType: 'blob', headers })
      .pipe(catchError((e) => this.processError(e)));
  }

  public uploadFile(params: any): Promise<any> {
    const formData = new FormData();
    formData.append('documento', params);
    const headers = new HttpHeaders().set(
      'x-access-token',
      this.getToken() || ''
    );
    return firstValueFrom(
      this.http
        .post<any>(this.apiUrl + 'files/create', formData, { headers })
        .pipe(catchError((e) => this.processError(e)))
    );
  }

  public uploadItemImage(
    itemId: string,
    file: File,
    tipoFile: string = 'imagen',
    tipoEvento: string = 'item'
  ): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('idItem', itemId);
    formData.append('tipoFile', tipoFile);
    formData.append('nombre', file.name);
    formData.append('habilitado', 'true');
    const headers = new HttpHeaders().set(
      'x-access-token',
      this.getToken() || ''
    );
    return this.http
      .post<any>(this.apiUrl + 'itemImage/create', formData, { headers })
      .pipe(catchError((e) => this.processError(e)));
  }

  public updateFile(params: any, idFile: any): Promise<any> {
    const formData = new FormData();
    formData.append('documento', params);
    const headers = new HttpHeaders().set(
      'x-access-token',
      this.getToken() || ''
    );
    return firstValueFrom(
      this.http
        .put<any>(this.apiUrl + 'files/update/' + idFile, formData, { headers })
        .pipe(catchError((e) => this.processError(e)))
    );
  }

  public uploadFirmware(url: string, formData: any): Observable<any> {
    const headers = new HttpHeaders().set(
      'x-access-token',
      this.getToken() || ''
    );
    return this.http
      .post<any>(this.apiUrl + url, formData, { headers })
      .pipe(catchError((e) => this.processError(e)));
  }

  public sendTrama(url: string, data: string): Observable<any> {
    return this.http
      .post<any>(`${environment.API_TILE}${url}`, data, {
        headers: this.authenticationHeaders,
      })
      .pipe(catchError((e) => this.processError(e)));
  }

  public getItemLocation(url: string): Observable<any> {
    return this.http
      .get<any>(`${environment.API_TILE}${url}`, {
        headers: this.authenticationHeaders,
      })
      .pipe(catchError((e) => {
        if (e.status === 400) {
          const errorMessage = e.error?.message || 'Error al obtener la ubicación del item';
          this._alertDialogService.fnMostrarToastAlertMD({ message: errorMessage, iconType: 'error' });
          return throwError(() => e);
        } else {
          this.processError(e);
        }
    }));
  }

  public getPersonLocation(url: string): Observable<any> {
    return this.http
      .get<any>(`${environment.API_TILE}${url}`, {
        headers: this.authenticationHeaders,
      })
      .pipe(catchError((e) => {
        if (e.status === 404) {
          const errorMessage = e.error?.message || 'Error al obtener la ubicación del item';
          this._alertDialogService.fnMostrarToastAlertMD({ message: errorMessage, iconType: 'error' });
          return throwError(() => e);
        } else {
          this.processError(e);
        }
      }));
  }

  public getItemsInMap(url: string): Observable<any> {
    return this.http
      .get<any>(`${environment.API_TILE}${url}`, {
        headers: this.authenticationHeaders,
      })
      .pipe(catchError((e) => this.processError(e)));
  }

  public getAuthHeaders(): HttpHeaders {
    return this.authenticationHeaders;
  }

  public putMantenimiento<T>(entityName: string, entity: T): Promise<any> {
    return firstValueFrom(
      this.http
        .put<any>(this.apiMantenimiento + entityName, entity, {
          headers: this.authenticationHeaders,
        })
        .pipe(catchError((e) => this.processError(e)))
    );
  }

  public getMantenimiento<T>(entityName: string, params?: any): Promise<any> {
    return firstValueFrom(
      this.http
        .get<any>(this.apiMantenimiento + entityName, {
          headers: this.authenticationHeaders,
          params,
        })
        .pipe(catchError((e) => this.processError(e)))
    );
  }

  public getGeofencing<T>(entityName: string, params?: any): Promise<any> {
    return firstValueFrom(
      this.http
        .get<any>(this.apiGeofencing + entityName, {
          headers: this.authenticationHeaders,
          params,
        })
        .pipe(catchError((e) => this.processError(e)))
    );
  }

  public createGeofencing<T>(entityName: string, entity: T): Promise<any> {
    return firstValueFrom(
      this.http
        .post<any>(this.apiGeofencing + entityName, entity, {
          headers: this.authenticationHeaders,
        })
        .pipe(catchError((e) => this.processError(e)))
    );
  }

  public editGeofencing<T>(entityName: string, entity: T): Promise<any> {
    return firstValueFrom(
      this.http
        .put<any>(this.apiGeofencing + entityName, entity, {
          headers: this.authenticationHeaders,
        })
        .pipe(catchError((e) => this.processError(e)))
    );
  }

  public getStatusByCompanyId<T>(
    idEmpresa: number,
    idTipoEstado?: number,
    habilitado: boolean = true
  ): Promise<any> {
    const params: any = { habilitado };
    if (idTipoEstado !== undefined && idTipoEstado !== null)
      params.idTipoEstado = idTipoEstado;
    return firstValueFrom(
      this.http
        .get<any>(
          `${this.apiUrl}companyStatus/getStatusByCompanyId/${idEmpresa}`,
          { headers: this.authenticationHeaders, params }
        )
        .pipe(catchError((e) => this.processError(e)))
    );
  }

  public getXInclude<T>(
    entityName: string,
    oTypeXInclude: any,
    params?: any
  ): Promise<any> {
    const headers = this.authenticationHeaders.set(
      'x-include',
      JSON.stringify(oTypeXInclude)
    );
    return firstValueFrom(
      this.http
        .get<any>(this.apiUrl + entityName, { headers, params })
        .pipe(catchError((e) => this.processError(e)))
    );
  }

  public saveDeadmanBitacora<T>(oDataDeadman: any): Promise<any> {
    return firstValueFrom(
      this.http
        .put<any>(
          `${this.apiUrl}bitacoraDeadman/update/${oDataDeadman.idBitacoraDeadman}`,
          { idEstado: oDataDeadman.idEstado, fechaRevisionEvento: new Date() },
          { headers: this.authenticationHeaders }
        )
        .pipe(catchError((e) => this.processError(e)))
    );
  }

  private processError(error: any): Observable<never> {
    let errorMessage = '';
    if (error.error instanceof ErrorEvent) {
      errorMessage = `Error del cliente: ${error.error.message}`;
    } else {
      errorMessage = `Error: ${error.status}\n- ${error.message}`;

      if (error.status === 401) {
        console.warn('Sesión expirada o no autorizada. Redirigiendo...');
      }


    }

    this._alertDialogService.fnMostrarAlertModal({ message: errorMessage, iconType: 'error' });
    return throwError(() => error);
  }
}
